import { useEffect, useState } from 'react';
import { AppModal } from 'shared/UI/UIKit/Modal';
import { useAppForm } from 'shared/UI/UIKit/Form';
import { getSubjectsList } from 'services/api/subjects';
import { useGetEducationClassListQuery, useUpdateTeacherWorkloadMutation } from 'shared/api';
import { TeacherWorkloadModalFooter } from 'entities/teacher-workload/modal-footer';
import {
  TeacherWorkloadForm,
  apiWorkDaysToForm,
  formWorkDaysToApi,
} from 'entities/teacher-workload/form';
import type { Subject } from 'services/api/subjects';
import type { TeacherWorkload } from 'shared/api';
import type { SubjectRow, TeacherWorkloadFormValues } from 'entities/teacher-workload/form/types';

type EditTeacherWorkloadModalProps = {
  visible: boolean;
  hide: () => void;
  onSuccess: () => void;
  workload: TeacherWorkload | null;
};

export const EditTeacherWorkloadModal = ({
  visible,
  hide,
  onSuccess,
  workload,
}: EditTeacherWorkloadModalProps) => {
  const { data: educationClasses } = useGetEducationClassListQuery({ size: 1000 });

  const [form] = useAppForm<TeacherWorkloadFormValues>();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [initialDataSource, setInitialDataSource] = useState<SubjectRow[]>([]);
  const [updateTeacherWorkload, { isLoading: isSubmitting }] = useUpdateTeacherWorkloadMutation();

  useEffect(() => {
    if (visible && workload) {
      setLoading(true);
      getSubjectsList()
        .then((res) => {
          setSubjects(res.content);
        })
        .finally(() => {
          setLoading(false);
        });

      form.setFieldsValue({
        code: workload.code,
        preferredRoomIds: workload.rooms?.map((r) => r.id) ?? [],
        subjectRows: workload.qualifications.map((s) => ({
          key: `${s.subjectId}`,
          subjectId: s.subjectId,
          workloadHours: s.workloadHours,
          educationalClassIds: s.educationalClassIds,
        })),
        workDays: apiWorkDaysToForm(workload.workDays),
      });

      setInitialDataSource(
        workload.qualifications.map((s) => ({
          key: `${s.subjectId}`,
          subjectId: s.subjectId,
          workloadHours: s.workloadHours,
          educationalClassIds: s.educationalClassIds,
        })),
      );
    }
  }, [visible, workload, form]);

  const hideModal = () => {
    form.resetFields();
    setError(null);
    hide();
  };

  const onSubmit = () => {
    if (!workload) return;

    form
      .validateFields()
      .then((values) => {
        const formSubjects = values.subjectRows || [];

        const updateSubjects = formSubjects
          .map((s) => ({
            subjectId: s.subjectId!,
            workloadHours: s.workloadHours!,
            educationalClassIds: s.educationalClassIds!,
          }))
          //TODO: Временно удаляем пустые классы, для проверки удобства пользователей - 28.08.2026
          .filter(
            (subject) =>
              Array.isArray(subject.educationalClassIds) && subject.educationalClassIds.length,
          );

        updateTeacherWorkload({
          id: workload.id,
          qualifications: updateSubjects,
          preferredRoomIds: values?.preferredRoomIds ?? [],
          code: values.code,
          workDays: formWorkDaysToApi(values.workDays),
        })
          .unwrap()
          .then(() => {
            hideModal();
            onSuccess();
          });
      })
      .catch((err) => {
        console.log('Form validation failed:', err);
        setError(err);
      });
  };

  const footer = (
    <TeacherWorkloadModalFooter hideModal={hideModal} onSubmit={onSubmit} title="Сохранить" />
  );

  const content = (
    <TeacherWorkloadForm
      form={form}
      availableSubjects={subjects}
      subjectsLoading={loading}
      error={error}
      setError={setError}
      initialDataSource={initialDataSource}
      availableEducationClasses={educationClasses?.content || []}
    />
  );

  return (
    <AppModal
      visible={visible}
      onCancel={hideModal}
      content={content}
      destroyOnClose
      footer={footer}
      loading={isSubmitting}
      title="Изменить нагрузку учителя"
      maskClosable={false}
      size="auto"
      wrapStyle={{ width: 'auto', alignItems: 'center' }}
      style={{ width: 'auto' }}
    />
  );
};
